// src/api/admin.ts
import { AxiosInstance } from 'axios';
import { useApi } from '../context/ApiProvider';
import type { ScheduleMeetingType } from '../lib/schedule/meetingTypes';

export type Role = 'admin' | 'agent' | 'client' | 'candidate';
export type AssignableRole = 'admin' | 'agent' | 'client';
export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'client' | 'dead';
export type LeadSource = 'manual' | 'checkout' | 'checkout_abandoned' | 'other';

export interface UserRecord {
  id: string;
  email: string;
  name?: string | null;
  businessName?: string | null;
  workspaceOrgId?: string | null;
  workspaceRole?: 'owner' | 'admin' | 'agent' | 'client' | 'viewer' | 'member' | null;
  workspaceDisplayName?: string | null;
  workspaceAvatarURL?: string | null;
  workspaceOrganizationType?: 'individual' | 'business' | null;
  role: Role;
  banned?: boolean;
  online?: boolean;        // NEW (presence mirror in Firestore)
  lastActiveAt?: number;   // NEW (epoch ms)
}

export interface UserDetails extends UserRecord {
  name?: string;
  photoURL?: string;
  firstName?: string | null;
  lastName?: string | null;
  userType?: 'business' | 'individual' | null;
  contactPhone?: string | null;
  bio?: string | null;
  discordHandle?: string | null;
  portfolioWebsite?: string | null;
  businessName?: string | null;
  businessAddress?: string | null;
  industry?: string | null;
  businessWebsite?: string | null;
  businessPhone?: string | null;
  pointOfContact?: string | null;
  team?: string | null;
  teamBrainNodeId?: string | null;
  title?: string | null;
  supervisorUid?: string | null;
  supervisorName?: string | null;
  supervisorPhotoURL?: string | null;
  agentTitle?: string | null;
  allowedMeetingTypes?: string[];
  scheduleMeetingTypeOptions?: Array<{
    type: ScheduleMeetingType;
    label: string;
  }>;
  detailsMode?: 'client' | 'operator' | 'basic';
  assignedAgents?: Array<{
    agentUid: string;
    agentEmail: string;
    agentName: string | null;
    agentTitle: string | null;
    assignedAt: string;
  }>;
  assignableAgents?: Array<{
    uid: string;
    email: string;
    name: string | null;
    title: string | null;
  }>;
  organizationRoles?: Array<{
    orgId: string;
    orgName: string;
    role: string;
  }>;
  assignedClients?: Array<{
    uid: string;
    email: string;
    name: string | null;
    businessName: string | null;
    assignedAt: string;
  }>;
  assignableClients?: Array<{
    uid: string;
    email: string;
    name: string | null;
    businessName: string | null;
  }>;
  openTicketStats?: {
    open: number;
    inProgress: number;
    blocked: number;
    totalOpen: number;
  };
  availableAgentTitles?: string[];
  assignableSupervisors?: Array<{
    uid: string;
    email: string;
    name: string | null;
    photoURL: string | null;
    title: string | null;
    role: Exclude<Role, 'candidate'>;
  }>;
  createdAt?: number;
  lastLoginAt?: number;
  isPaid?: boolean;
  tags?: string[];
  notesCount?: number;
  stripe?: {
    customerId?: string;
    subscriptions?: { id: string; status: string; priceId: string; currentPeriodEnd: number }[];
  };
  subscriptionStatus?: string | null;
  subscriptionType?: string | null;
  purchasedServices?: Array<{
    serviceTitle: string;
    tierName: string | null;
    label: string;
    serviceCode: string | null;
    tierSkuId: string | null;
    status: string;
    startsAt: string;
    updatedAt: string;
  }>;
}

export interface ExportUsersRequest {
  q?: string;
  role?: Role;
  online?: boolean;
  ids?: string[];   // when blasting to selected users
}
export interface ExportUsersResponse {
  count: number;
  emails?: string[]; // server may omit for privacy; we only need count on UI
}

export interface BlastRequest {
  filters: ExportUsersRequest;
  subject: string;
  preheader?: string;
  html?: string;           // OPTIONAL: raw HTML content
  templateId?: number;     // OPTIONAL: use a Mailchimp template
  testEmails?: string[];   // OPTIONAL: send tests first
}

export interface BlastResult {
  campaignId: string;
  segmentId?: number;
}

export interface LeadRecord {
  id: string;
  name: string;
  businessName?: string | null;
  email: string;
  phone?: string | null;
  website?: string | null;
  industry?: string | null;
  serviceInterest?: string | null;
  notes?: string | null;
  status: LeadStatus;
  source: LeadSource;
  cartAbandoned: boolean;
  checkoutStatus: string;
  customerType: 'business' | 'individual';
  clientUid?: string | null;
  clientName?: string | null;
  assignedAgentUid?: string | null;
  assignedAgentName?: string | null;
  assignedAgentRole?: 'admin' | 'agent' | 'client' | null;
  createdAt: string;
  updatedAt: string;
  paidAt?: string | null;
  convertedToClient: boolean;
  hasServiceSubscription: boolean;
  stripeCheckoutSessionId?: string | null;
  stripeSubscriptionId?: string | null;
}

export interface LeadsListResponse {
  leads: LeadRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateLeadRequest {
  name: string;
  email: string;
  businessName?: string;
  phone?: string;
  website?: string;
  industry?: string;
  serviceInterest?: string;
  notes?: string;
  status?: LeadStatus;
  assignedAgentUid?: string;
  clientUid?: string;
}

export interface UpdateLeadRequest {
  name?: string;
  email?: string;
  businessName?: string;
  phone?: string;
  website?: string;
  industry?: string;
  serviceInterest?: string;
  notes?: string;
  status?: LeadStatus;
  assignedAgentUid?: string;
  clientUid?: string;
}

export interface InviteUserRequest {
  email: string;
  role: AssignableRole;
  businessName?: string;
}

export interface InviteUserResponse {
  ok: boolean;
  uid: string;
  email: string;
  role: AssignableRole;
  createdAuthUser: boolean;
  onboardingEmailSent: boolean;
  onboardingLink?: string;
}

export interface UpdateUserBusinessDetailsRequest {
  firstName?: string;
  lastName?: string;
  userType?: 'business' | 'individual' | '';
  contactPhone?: string;
  discordHandle?: string;
  portfolioWebsite?: string;
  bio?: string;
  businessName?: string;
  businessAddress?: string;
  industry?: string;
  businessWebsite?: string;
  businessPhone?: string;
  pointOfContact?: string;
  team?: string;
  teamBrainNodeId?: string | null;
  title?: string;
  supervisorUid?: string;
}

export interface UpdateClientAssignmentRequest {
  agentUid?: string | null;
  action?: 'assign' | 'remove';
}

export interface UpdateAgentTitleRequest {
  title: string;
}

export interface UpdateAgentMeetingPermissionsRequest {
  allowedMeetingTypes: ScheduleMeetingType[];
}

export const AdminApi = (api: AxiosInstance) => ({
  listUsers: (params: { q: string; role: Role | 'all'; page: number; online?: boolean }) =>
    api.get<{ users: UserRecord[] }>('/admin/users', { params }).then(r => r.data.users),

  getUser: (uid: string) =>
    api.get<UserDetails>(`/admin/users/${uid}`).then(r => r.data),

  updateRole: (uid: string, role: AssignableRole) =>
    api.patch(`/admin/users/${uid}/role`, { role }),

  updateBan: (uid: string, banned: boolean) =>
    api.patch(`/admin/users/${uid}/ban`, { banned }),

  updateUserBusinessDetails: (uid: string, payload: UpdateUserBusinessDetailsRequest) =>
    api.patch(`/admin/users/${uid}/details`, payload).then((r) => r.data),

  updateClientAssignment: (uid: string, payload: UpdateClientAssignmentRequest) =>
    api.patch(`/admin/users/${uid}/client-assignment`, payload).then((r) => r.data),

  updateAgentTitle: (uid: string, payload: UpdateAgentTitleRequest) =>
    api.patch(`/admin/users/${uid}/agent-title`, payload).then((r) => r.data),

  updateAgentMeetingPermissions: (
    uid: string,
    payload: UpdateAgentMeetingPermissionsRequest,
  ) =>
    api
      .patch(`/admin/users/${uid}/meeting-permissions`, payload)
      .then((r) => r.data),

  listAgentTitles: () =>
    api.get<{ titles: string[] }>('/admin/agent-titles').then((r) => r.data.titles),

  inviteUser: (payload: InviteUserRequest) =>
    api.post<InviteUserResponse>('/admin/users/invite', payload).then(r => r.data),

  exportUsers: (params: ExportUsersRequest) =>
    api.get<ExportUsersResponse>('/admin/users/export', { params }).then(r => r.data),

  sendBlast: (payload: BlastRequest) =>
    api.post<BlastResult>('/admin/blasts/mailchimp', payload).then(r => r.data),

  listLeads: (params: {
    q?: string;
    status?: LeadStatus | 'all';
    source?: LeadSource | 'all';
    page?: number;
  }) =>
    api.get<LeadsListResponse>('/admin/leads', { params }).then((r) => r.data),

  getLead: (leadId: string) =>
    api.get<LeadRecord>(`/admin/leads/${leadId}`).then((r) => r.data),

  createLead: (payload: CreateLeadRequest) =>
    api.post<LeadRecord>('/admin/leads', payload).then((r) => r.data),

  updateLead: (leadId: string, payload: UpdateLeadRequest) =>
    api.patch<LeadRecord>(`/admin/leads/${leadId}`, payload).then((r) => r.data),

  deleteLead: (leadId: string) =>
    api.delete<{ ok: boolean; id: string }>(`/admin/leads/${leadId}`).then((r) => r.data),
});

export const useAdminApi = () => AdminApi(useApi());
